import React, { useState, useContext, useEffect } from "react";
import UserContext from "../../../assets/user_context";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DatePicker } from "@mui/x-date-pickers";
import { TextField, InputAdornment } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import styled from "@emotion/styled";
import { primary_highlight_color } from "../../../constants/color_constants";
import {
  collection,
  addDoc,
  doc,
  updateDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import {
  formatCurrency,
  generateRandomId,
  returnCurrency,
  returnCurrencyInput,
} from "../../../utilities/general_util";
import { createFinancialDate } from "../../../utilities/dateUtils";

const StyledHelperTextField = styled(TextField)`
  .MuiFormHelperText-root.Mui-error {
    color: #f44336; // or whatever the default Material-UI error color you want
  }

  .MuiFormHelperText-root {
    color: ${primary_highlight_color};
    font-size: 0.8rem;
  }
`;

export const addJournalEntry = async ({
  accountEntryData,
  accounts,
  funds,
  accountId,
  user,
  org,
  db,
  setTransactions,
  newAccountOverride,
}) => {
  //Need to create a journalEntry that will be used to create the starting balance from equity accounts

  const lines = [];
  console.log("accountEntryData: ", accountEntryData);
  console.log("accountTypeOverride: ", newAccountOverride);
  Object.keys(accountEntryData.fundStartingBalances).forEach((fundID) => {
    const fundBalance = accountEntryData.fundStartingBalances[fundID];
    if (fundBalance !== "" && Number(fundBalance) !== 0) {
      lines.push({
        //Equity account line
        account: accounts.find(
          (account) =>
            account.accountNumber ===
            funds.find((fund) => fund.id === fundID).fundNumber,
        ).id,
        fund: fundID,
        amount: accountEntryData.fundStartingBalances[fundID],
        sign: newAccountOverride?.accountType
          ? newAccountOverride.accountType === "Assets"
            ? "credit"
            : "debit"
          : accounts.find((account) => account.id === accountId).accountType ===
              "Assets"
            ? "credit"
            : "debit",
        comment: `Starting Balance for ${
          newAccountOverride?.accountName
            ? newAccountOverride.accountName
            : accountEntryData.accountName
        }`,
        contact: null,
      });
      lines.push({
        //Account line
        account: accountId,
        fund: fundID,
        amount: accountEntryData.fundStartingBalances[fundID],
        sign: newAccountOverride?.accountType
          ? newAccountOverride.accountType === "Assets"
            ? "debit"
            : "credit"
          : accounts.find((account) => account.id === accountId).accountType ===
              "Assets"
            ? "debit"
            : "credit",
        comment: `Starting Balance for ${
          newAccountOverride?.accountName
            ? newAccountOverride.accountName
            : accountEntryData.accountName
        }`,
        contact: null,
      });
    }
  });
  console.log("Lines: ", lines);
  console.log("Date: ", accountEntryData.startingBalanceDate);
  // Pre-compute query arrays so the document is immediately queryable.
  // The activity tracker skips array enhancement when accounts already exists,
  // so we must include all four arrays (accounts, funds, contacts, labels).
  const queryArrays = {
    accounts: [...new Set(lines.map((l) => l.account).filter(Boolean))],
    funds: [...new Set(lines.map((l) => l.fund).filter(Boolean))],
    contacts: [...new Set(lines.map((l) => l.contact).filter(Boolean))],
    labels: [...new Set(lines.map((l) => l.label).filter(Boolean))],
  };

  const journalEntryData = {
    // Use createFinancialDate to store at UTC noon, preventing timezone-related date shifts
    date: createFinancialDate(accountEntryData.startingBalanceDate),
    addTimestamp: true,
    memo: `Starting Balance for ${
      newAccountOverride?.accountName
        ? newAccountOverride.accountName
        : accounts.find((account) => account.id === accountId).accountName
    }`,
    lines: lines,
    ...queryArrays,
    userMadeChange: user.uid,
    journalEntry: true,
    isStartingBalance: true,
  };
  console.log(journalEntryData);
  try {
    const journalEntriesRef = collection(db, "orgs", org, "journalEntries");

    // Check if a starting balance transaction already exists for this account
    const existingStartingBalanceQuery = query(
      journalEntriesRef,
      where("isStartingBalance", "==", true),
      where("accounts", "array-contains", accountId),
    );
    const existingSnapshot = await getDocs(existingStartingBalanceQuery);

    let journalEntryRef;
    if (!existingSnapshot.empty) {
      // Update existing starting balance transaction
      const existingDoc = existingSnapshot.docs[0];
      const existingData = existingDoc.data();
      journalEntryRef = existingDoc.ref;

      // CRITICAL: Preserve 'cleared' status from existing lines to prevent accidental un-clearing of reconciled transactions
      // Match lines by account+fund combination to preserve cleared status
      const updatedLines = journalEntryData.lines.map((newLine) => {
        const matchingExistingLine = existingData.lines?.find(
          (existingLine) =>
            existingLine.account === newLine.account &&
            existingLine.fund === newLine.fund &&
            existingLine.sign === newLine.sign,
        );

        // If we found a matching line with a cleared status, preserve it
        if (matchingExistingLine?.cleared !== undefined) {
          console.log(
            `Preserving cleared status for line: account=${newLine.account}, fund=${newLine.fund}, cleared=${matchingExistingLine.cleared}`,
          );
          return {
            ...newLine,
            cleared: matchingExistingLine.cleared,
          };
        }

        return newLine;
      });

      // The Activity Tracker would silently revert amount changes on cleared lines — catch it here instead
      const clearedLineAmountChanged = existingData.lines?.some(
        (existingLine) => {
          if (!existingLine.cleared) return false;
          const matchingNewLine = updatedLines.find(
            (nl) =>
              nl.account === existingLine.account &&
              nl.fund === existingLine.fund &&
              nl.sign === existingLine.sign,
          );
          // Cleared line was removed or its amount changed
          return !matchingNewLine || matchingNewLine.amount !== existingLine.amount;
        },
      );

      if (clearedLineAmountChanged) {
        throw new Error(
          "Cannot modify starting balance: Some lines have been cleared in a bank reconciliation. " +
            "Please remove all reconciliations for this account before changing the starting balance.",
        );
      }

      // Update the transaction with preserved cleared statuses
      // Recompute query arrays from the updated lines to keep them consistent
      const updatedQueryArrays = {
        accounts: [...new Set(updatedLines.map((l) => l.account).filter(Boolean))],
        funds: [...new Set(updatedLines.map((l) => l.fund).filter(Boolean))],
        contacts: [...new Set(updatedLines.map((l) => l.contact).filter(Boolean))],
        labels: [...new Set(updatedLines.map((l) => l.label).filter(Boolean))],
      };
      const updatedJournalEntryData = {
        ...journalEntryData,
        lines: updatedLines,
        ...updatedQueryArrays,
      };

      await updateDoc(journalEntryRef, updatedJournalEntryData);
      console.log(
        "Updated existing starting balance transaction with preserved cleared statuses:",
        journalEntryRef.id,
      );
    } else {
      // Create new starting balance transaction. createdAt is only set on
      // creation so same-day register sort is stable across later edits.
      journalEntryRef = await addDoc(journalEntriesRef, {
        ...journalEntryData,
        createdAt: new Date(),
      });
      console.log(
        "Created new starting balance transaction:",
        journalEntryRef.id,
      );
    }

    //update the account doc to have startingBalance=true
    const accountRef = doc(db, "orgs", org, "accounts", accountId);
    await updateDoc(accountRef, { startingBalance: true });

    return journalEntryRef;
  } catch (e) {
    console.error("Error adding/updating StartingBalance journal entry: ", e);
    throw e;
  }
};

// Disabled funds must not be offered for new starting-balance money, but a
// disabled fund that an existing starting-balance line already references stays
// visible/editable so the data isn't stranded or silently rewritten.
export const getSelectableStartingBalanceFunds = (
  funds,
  fundStartingBalances,
) =>
  funds.filter((fund) => {
    if (!fund.disabled) return true;
    const existing = fundStartingBalances?.[fund.id];
    return existing !== undefined && existing !== "" && Number(existing) !== 0;
  });

const StartingBalance = ({
  accountEntryData,
  setAccountEntryData,
  entryErrors,
  entryFeedback,
  location,
  accountsWithoutSB,
}) => {
  const { funds } = useContext(UserContext);
  const theme = useTheme();
  const [remainingStartingToAssign, setRemainingStartingToAssign] = useState({
    string: "",
    num: 0,
  });
  const [startingBalanceDisplay, setStartingBalanceDisplay] = useState("");
  const [fundStartingBalancesDisplay, setFundStartingBalancesDisplay] =
    useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    const match = name.split("-")[1];
    // TextField name={`startingBalance-${fund.id}`}

    if (name === "startingBalance") {
      // let inputVal = value.replace(/[^0-9]/g, "");
      // inputVal = inputVal.padStart(2, "0");
      // const newCentsValue = parseInt(inputVal, 10);
      const { centsInt: newCentsValue, formattedDollarValue } =
        returnCurrencyInput(value);

      setAccountEntryData((prevData) => ({
        ...prevData,
        startingBalance: newCentsValue,
        // Initialize 'fundStartingBalances' if it doesn't exist
        fundStartingBalances:
          prevData.fundStartingBalances ||
          funds
            .filter((fund) => !fund.disabled)
            .reduce((acc, fund) => {
              acc[fund.id] = ""; // Initialize each fund's starting balance as an empty string
              return acc;
            }, {}),
      }));
      setStartingBalanceDisplay(formattedDollarValue);
    } else if (match) {
      const fundId = match;
      // let inputVal = value.replace(/[^0-9]/g, "");
      // inputVal = inputVal.padStart(2, "0");
      // const newCentsValue = parseInt(inputVal, 10);
      const { centsInt: newCentsValue, formattedDollarValue } =
        returnCurrencyInput(value);

      setAccountEntryData((prevData) => ({
        ...prevData,
        fundStartingBalances: {
          ...prevData.fundStartingBalances,
          [fundId]: newCentsValue,
        },
      }));

      setFundStartingBalancesDisplay((prevDisplay) => ({
        ...prevDisplay,
        [fundId]: formattedDollarValue,
      }));
    }
  };

  // const calcRemainingStartingToAssign = () => {
  //   if (accountEntryData.startingBalance > 0) {
  //     const sumOfFundStartingBalances = Object.values(
  //       accountEntryData.fundStartingBalances,
  //     ).reduce((acc, curr) => acc + (curr || 0), 0);

  //     const remainder =
  //       accountEntryData.startingBalance - sumOfFundStartingBalances;
  //     setRemainingStartingToAssign(returnCurrency(remainder));
  //   }
  // };

  const calcRemainingStartingToAssign = () => {
    if (accountEntryData.startingBalance > 0) {
      const fundStartingBalances = Object.values(
        accountEntryData.fundStartingBalances,
      );

      const sumOfFundStartingBalances = fundStartingBalances.reduce(
        (acc, curr) => {
          if (curr !== "") {
            return acc + (curr || 0);
          } else {
            return acc;
          }
        },
        0,
      );

      const remainder =
        accountEntryData.startingBalance - sumOfFundStartingBalances;
      console.log("remainder", remainder, returnCurrency(remainder));
      setRemainingStartingToAssign({
        string: returnCurrency(Math.abs(remainder)),
        num: remainder,
      });
    }
  };

  useEffect(
    () => {
      console.log("accountEntryData changed", accountEntryData);
      //Reset display values when accountEntryData changes
      setStartingBalanceDisplay("");
      setFundStartingBalancesDisplay({});
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accountsWithoutSB],
  );

  useEffect(() => {
    console.log("entryErrors: ", entryErrors);
  }, [entryErrors]);

  //Error style for date picker since prop is not working
  const errorStyleForDate = entryErrors.startingBalanceDate
    ? {
        color: "red",
        "& .MuiOutlinedInput-root": {
          "& fieldset": {
            borderColor: "red",
          },
        },
        "& .MuiInputLabel-root": {
          // Custom style for the label
          color: "red",
        },
      }
    : {};

  return (
    <>
      <StyledHelperTextField
        sx={{
          width: "350px",
          fontSize: "22px",
        }}
        label="Starting Balance"
        name="startingBalance"
        variant="outlined"
        color={theme.palette.mode === "dark" ? "primary" : "secondary"}
        value={startingBalanceDisplay}
        onChange={handleChange}
        error={entryErrors.startingBalanceDate}
        disabled={typeof accountDataToEdit === "object" ? true : false}
        helperText={
          remainingStartingToAssign.num > 0
            ? `You have ${remainingStartingToAssign.string} left to assign to funds`
            : remainingStartingToAssign.num < 0
              ? `You have assigned too many funds by  ${remainingStartingToAssign.string}`
              : ""
        }
        InputProps={{
          startAdornment: <InputAdornment position="start">$</InputAdornment>,
        }}
        onBlur={() =>
          setStartingBalanceDisplay(
            formatCurrency(accountEntryData.startingBalance),
          )
        }
      />
      {accountEntryData.startingBalance > 0 &&
        Object.keys(accountEntryData.fundStartingBalances || {}).length > 0 && (
          <>
            <div style={{ height: "20px" }} />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label={<span>Date *</span>}
                sx={errorStyleForDate}
                value={accountEntryData.startingBalanceDate}
                onChange={(event) => {
                  console.log(event);
                  const value = event.$d;
                  const newAccountEntryData = {
                    ...accountEntryData,
                  };
                  newAccountEntryData.startingBalanceDate = value;
                  console.log(newAccountEntryData);
                  setAccountEntryData(newAccountEntryData);
                }}
                disableFuture
                disabled={location === "onboarding" ? true : false}
                variant="outlined"
                color={theme.palette.mode === "dark" ? "primary" : "secondary"}
                renderInput={(params) => (
                  <TextField
                    sx={{ marginTop: "20px" }}
                    {...params}
                    error={true}
                    variant="outlined"
                    color={
                      theme.palette.mode === "dark" ? "primary" : "secondary"
                    }
                  />
                )}
              />
            </LocalizationProvider>
            {getSelectableStartingBalanceFunds(
              funds,
              accountEntryData.fundStartingBalances,
            ).map((fund) => (
              <TextField
                key={fund.id}
                sx={{
                  width: "320px",
                  fontSize: "22px",
                  marginTop: "20px",
                  marginLeft: "30px",
                  // Match funds list styling for disabled funds (render_fund_row.jsx)
                  ...(fund.disabled && { opacity: 0.5 }),
                }}
                label={`Starting Balance for ${fund.fundName}${
                  fund.disabled ? " (disabled)" : ""
                }`}
                name={`startingBalance-${fund.id}`}
                variant="outlined"
                color={theme.palette.mode === "dark" ? "primary" : "secondary"}
                value={fundStartingBalancesDisplay[fund.id]}
                onChange={handleChange}
                error={entryErrors.startingBalance}
                disabled={typeof accountDataToEdit === "object" ? true : false}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                onBlur={() => {
                  calcRemainingStartingToAssign();
                  setFundStartingBalancesDisplay((prevDisplay) => ({
                    ...prevDisplay,
                    [fund.id]: formatCurrency(
                      accountEntryData.fundStartingBalances[fund.id],
                    ),
                  }));
                }}
                helperText={entryFeedback.startingBalance}
              />
            ))}
          </>
        )}
    </>
  );
};

export default StartingBalance;
